import { useContext, useEffect, useRef, useState } from 'react';
import { useDataModel } from '@thd-nucleus/data-sources';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import {
  triggerLifeCycleEvent, getProductIndex, onLifeCycleEvent, getProduct, getSelectableVariantsIds,
  getBundleComponentItemIds, shouldUpdateAccessories, objectWithDefaultValue, updateSelectableVariantRef,
  isAccessoriesLoading, getProductState, getBundleProductIds, getNewProductsData, shouldUpdateProductsRef
} from '../utils/product-bundle-utils';
import {
  getBundleAccessoriesFromBundleProduct,
  getItemIdsFromBundleAccessories,
  getItemIdsFromBundleComponents,
  getUpdatedAccessoriesProduct,
  getUpdatedProduct,
  getUpdatedProducts,
  isMissingProducts,
  separateProductsByType
} from '../utils/bundle-features-utils';
import { getFilterResponseFlag } from '../utils/product-unavailable-utils';
import { PRODUCTS_CHANGED, ACCESSORIES_CHANGED, UPDATE_PRICE_ADJUSTMENT, ALERT_TYPES } from '../constants/index';
import { useCheckAvailability } from './useCheckAvailability';
import { useGetBundleConfigSwitch } from './useGetBundleConfigSwitch';

export const useBundleProducts = (
  bundleId, storeId, listenToProductsChange = true, isCustomerIdentified = false) => {
  const [productIds, setProductIds] = useState(null);
  const [productsState, setProductsState] = useState(null);
  const [accessoriesState, setAccessoriesState] = useState(null);
  const productsRef = useRef([]);
  const selectedAccessoriesRef = useRef([]);
  const updateProductRef = useRef(null);
  const selectableVariantProductsRef = useRef(null);
  const [selectedAccessories, setSelectedAccessories] = useState([]);

  const { deliveryZip } = useContext(ExperienceContext);
  const { storeZip } = useStore();
  const {
    configBundleProducts,
    configSelectableVariantProducts,
    configExperienceType,
    configBundleType,
    configBundleProductsDetails,
    enableAccessories,
    enableRemoveItemPromoImage
  } = useGetBundleConfigSwitch();

  const addAccessories = (accessoriesData) => {
    const output = {
      accessoriesData: objectWithDefaultValue(accessoriesData?.products, []),
      bundleProducts: productsRef?.current
    };
    triggerLifeCycleEvent(ACCESSORIES_CHANGED, output);
  };

  const swapBack = (productId, isVariantSwap) => {
    const i = getProductIndex(productId, productsRef.current);
    const product = productsRef.current[i];

    if (product?.swappedProduct) {
      const { swappedProduct, ...newSwappedProduct } = product;
      const newProduct = {
        ...swappedProduct,
        swappedProduct: newSwappedProduct,
        alertType: isVariantSwap ? ALERT_TYPES.SWAPPED_VARIANT : ALERT_TYPES.SWAPPED,
        ...(isVariantSwap && { isVariantSwap: true })
      };

      let newProducts = [...productsRef.current];
      newProducts[i] = newProduct;
      productsRef.current = newProducts;
      setProductsState(newProducts);
      triggerLifeCycleEvent(PRODUCTS_CHANGED, { products: newProducts, runPriceAdjustment: true });
    }
  };

  const swapProducts = (prevProductId, newProductId, isVariantSwap) => {
    const i = getProductIndex(prevProductId, productsRef.current);
    if (!isVariantSwap && productsRef.current[i]?.swappedProduct?.itemId === newProductId) {
      swapBack(prevProductId);
      return;
    }
    if (i !== -1) {
      updateProductRef.current = {
        index: i,
        product: productsRef.current[i],
        ...(isVariantSwap && { isVariantSwap: true }),
        alertType: isVariantSwap ? ALERT_TYPES.SWAPPED_VARIANT : ALERT_TYPES.SWAPPED
      };
      productsRef.current[i] = {
        itemId: newProductId,
        swappedProduct: updateProductRef.current.product
      };
      setProductIds([newProductId]);
    }
  };

  const addProduct = (prevProductId, newProductId) => {
    const i = getProductIndex(prevProductId, productsRef.current);
    if (prevProductId === newProductId) {
      let updatedProduct = [...productsRef.current];
      updatedProduct[i] = {
        ...updatedProduct[i],
        removed: false,
        alertType: ALERT_TYPES.ADDED
      };
      productsRef.current = updatedProduct;
      setProductsState(updatedProduct);
      triggerLifeCycleEvent(PRODUCTS_CHANGED, { products: updatedProduct, runPriceAdjustment: true });
    } else {
      updateProductRef.current = {
        index: i,
        product: null,
        alertType: ALERT_TYPES.ADDED
      };
      setProductIds([newProductId]);
    }
  };

  const dismissAlert = (productId) => {
    const i = getProductIndex(productId, productsRef.current);
    if (i !== -1) {
      let newProducts = [...productsRef.current];
      newProducts[i] = {
        ...newProducts[i],
        alertType: null
      };
      productsRef.current = newProducts;
      setProductsState(newProducts);
      triggerLifeCycleEvent(PRODUCTS_CHANGED, { products: newProducts, runPriceAdjustment: false });
    }
  };

  const removeProduct = (productId) => {
    const i = getProductIndex(productId, productsRef.current);
    if (i !== -1) {
      let newProducts = [...productsRef.current];
      const { swappedProduct, ...newProduct } = objectWithDefaultValue(newProducts[i], {});
      newProducts[i] = {
        ...newProduct,
        removed: true,
        alertType: ALERT_TYPES.REMOVED
      };
      productsRef.current = newProducts;
      triggerLifeCycleEvent(PRODUCTS_CHANGED, { products: productsRef.current, runPriceAdjustment: true });
    }
  };

  useEffect(() => {
    if (!listenToProductsChange) {
      return;
    }
    onLifeCycleEvent(PRODUCTS_CHANGED, ({ output }) => {
      const { products, runPriceAdjustment } = output || {};
      setProductsState(products);
      if (productsRef?.current !== products) {
        productsRef.current = products;
      }
      if (runPriceAdjustment) {
        triggerLifeCycleEvent(UPDATE_PRICE_ADJUSTMENT, {
          accessoriesData: selectedAccessoriesRef.current,
          bundleProducts: productsRef.current
        });
      }
    });
    onLifeCycleEvent(ACCESSORIES_CHANGED, ({ output }) => {
      const { accessoriesData } = objectWithDefaultValue(output, {});
      setSelectedAccessories(accessoriesData);
      selectedAccessoriesRef.current = accessoriesData;
      triggerLifeCycleEvent(UPDATE_PRICE_ADJUSTMENT, {
        accessoriesData: selectedAccessoriesRef.current,
        bundleProducts: productsRef.current
      });
    });
  }, [listenToProductsChange]);

  const { data: productData, variables } = useDataModel('product', {
    variables: {
      itemId: bundleId,
      storeId,
      zipCode: deliveryZip || storeZip,
      isBrandPricingPolicyCompliant: isCustomerIdentified
    },
    skip: !bundleId
  });

  const bundleProduct = productData?.product;
  const bundleComponentItemIds = getBundleComponentItemIds(productData);
  const bundleItemIds = getItemIdsFromBundleComponents(productIds, bundleComponentItemIds, configBundleProducts);

  const selectableVariantsItemIds = getSelectableVariantsIds({ productData: bundleProduct,
    bundleItemIds,
    configSelectableVariantProducts });

  const bundleAccessories = getBundleAccessoriesFromBundleProduct(bundleProduct, enableAccessories);
  const accessoryItemIds = getItemIdsFromBundleAccessories(bundleAccessories);

  const bundleProductIds = getBundleProductIds(productIds, bundleItemIds, selectableVariantsItemIds, accessoryItemIds);

  const filterResponse = getFilterResponseFlag(bundleProduct);

  const skip = objectWithDefaultValue(!bundleProduct, !bundleProductIds.length);

  const { data, loading, error: productsError } = useDataModel('products', {
    variables: {
      itemIds: bundleProductIds,
      isBrandPricingPolicyCompliant: isCustomerIdentified,
      filterResponse
    },
    skip
  });

  const updatedProduct = getUpdatedProduct(
    getProduct(bundleProduct, configExperienceType, configBundleType, configBundleProducts),
    objectWithDefaultValue(data?.products, []),
    configExperienceType,
    configBundleType
  );

  const { data: checkAvailabilityData } = useCheckAvailability({ product: updatedProduct,
    itemIds: bundleProductIds });

  const updatedBundleProducts = getUpdatedProducts(updatedProduct, objectWithDefaultValue(data?.products, []),
    bundleComponentItemIds, configExperienceType, configBundleType, configBundleProductsDetails,
    checkAvailabilityData, selectableVariantsItemIds, accessoryItemIds, enableRemoveItemPromoImage);

  const {
    updatedProducts,
    accessoryProducts,
    selectableVariants
  } = separateProductsByType(updatedBundleProducts);

  selectableVariantProductsRef.current = updateSelectableVariantRef(selectableVariantProductsRef, selectableVariants,
    updatedProduct, checkAvailabilityData);

  useEffect(() => {
    productsRef.current = [];
  }, [isCustomerIdentified]);

  useEffect(() => {
    if (shouldUpdateProductsRef(data, updatedProduct, checkAvailabilityData)) {
      let newProductsData = getNewProductsData(productsRef, updatedProducts);
      if (updateProductRef?.current !== null) {
        const isIncomplete = isMissingProducts(bundleComponentItemIds, newProductsData, selectableVariantsItemIds);
        const isNewProduct = updateProductRef?.current.product === null;
        const product = objectWithDefaultValue((isNewProduct ? productsRef.current[updateProductRef?.current.index]
          : updateProductRef?.current?.product), {});
        const newProduct = {
          ...updatedProducts?.[0],
          swappedProduct: isNewProduct ? null : product,
          ...(updateProductRef?.current?.isVariantSwap && { isVariantSwap: true }),
          ...(updateProductRef?.current?.alertType && { alertType: updateProductRef.current.alertType }),
          isIncomplete,
          features: product?.features,
          quantity: objectWithDefaultValue(product?.quantity, 1),
          priority: objectWithDefaultValue(product?.priority, 0)
        };
        newProductsData[updateProductRef?.current.index] = newProduct;
        triggerLifeCycleEvent(PRODUCTS_CHANGED, { products: newProductsData, runPriceAdjustment: true });
        updateProductRef.current = null;
      }
      productsRef.current = newProductsData;
    }
  }, [data, checkAvailabilityData]);

  useEffect(() => {
    const updateAccessories = shouldUpdateAccessories(accessoryProducts, updatedProduct,
      bundleAccessories, accessoriesState);
    if (updateAccessories) {
      const result = getUpdatedAccessoriesProduct(
        accessoryProducts,
        updatedProduct,
        bundleAccessories,
        configExperienceType,
        configBundleType
      );
      setAccessoriesState(result);
    }
  }, [accessoryProducts, updatedProduct, bundleAccessories, configExperienceType, configBundleType, accessoriesState]);

  return {
    itemId: bundleId,
    product: updatedProduct,
    products: getProductState(productsState, updatedProducts),
    selectableVariantProducts: selectableVariantProductsRef?.current,
    accessories: accessoriesState,
    selectedAccessories,
    loadingAccessories: isAccessoriesLoading(loading, !accessoriesState, bundleAccessories?.length > 0),
    loading,
    error: false,
    productsError,
    variables,
    bundleProductsCount: bundleComponentItemIds?.length,
    swapProducts,
    swapBack,
    addProduct,
    removeProduct,
    dismissAlert,
    addAccessories
  };
};
